import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import { guidedBase } from './useGuidedResponse'
import type { Guide, GuideBody } from '../types/gotypes'

/**
 * Admin → People → Guided conversations (28 Oct 2026): every guide of the
 * customer, and New guide; for a reviewer (migration 000190), the guides
 * that name them. Writing is Manager only, on the server too
 * (goapi/pkg/server/guided.go).
 */

export const adminGuidesKey = (customer: string) => ['guided', customer, 'guides']

/** A new guide's body: a title and a goal; the rest is filled in on its page. */
export function newGuideBody(title: string, goal: string): GuideBody {
  return { title: title.trim(), goal: goal.trim(), intro: '', readers_note: '', jobs: [], points: [] }
}

export default function useAdminGuides(customer: string) {
  const queryClient = useQueryClient()
  const query = useQuery<Guide[], Error>({
    queryKey: adminGuidesKey(customer),
    enabled: Boolean(customer),
    queryFn: async () => {
      try {
        return (await axios.get<{ guides: Guide[] }>(`${guidedBase(customer)}/guides`)).data.guides ?? []
      } catch (error) {
        // 403: the caller is no longer a reviewer of any guide (their /me can
        // lag a removal), which the page shows as no guides, not an error.
        if ((error as { response?: { status?: number } })?.response?.status === 403) return []
        throw new Error(extractErrorMessage(error))
      }
    },
  })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  /** Create a guide; returns it, or null with createError set. */
  const handleCreate = async (body: GuideBody): Promise<Guide | null> => {
    setCreating(true)
    setCreateError('')
    try {
      const { data } = await axios.post<Guide>(`${guidedBase(customer)}/guides`, body)
      void queryClient.invalidateQueries({ queryKey: adminGuidesKey(customer) })
      return data
    } catch (error) {
      setCreateError(extractErrorMessage(error))
      return null
    } finally {
      setCreating(false)
    }
  }

  return {
    guides: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? '',
    creating,
    createError,
    handleCreate,
  }
}
